import React from 'react';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { motion } from 'framer-motion';
import {
  Trophy,
  Users,
  Plus,
  Trash2,
  Lock,
  Unlock,
  Calendar,
  Flag,
  Copy,
  X,
  Pause,
  PlayCircle,
  Pencil,
  Check,
  UserPlus,
  Search,
  Sparkles,
  RefreshCw,
  Ban,
  CalendarClock,
  ListOrdered,
  Swords,
} from 'lucide-react';
import { toast } from 'sonner';
import { championshipsApi, clubsApi } from '../../lib/api';
import { KnockoutPreviewDialog } from '../../components/KnockoutPreviewDialog';

export const ChampionshipsTab = ({
  isItalian,
  championships,
  clubs,
  expandedChampClubs,
  setExpandedChampClubs,
  removingClub,
  setShowCreateChampionshipDialog,
  handleGenerateSchedule,
  handleToggleRegistration,
  handleSuspendChampionship,
  handleCompleteChampionship,
  handleDeleteChampionship,
  handleRemoveClubFromChampionship,
  refreshData,
}) => {
  const [editingMaxTeams, setEditingMaxTeams] = React.useState(null);
  const [maxTeamsValue, setMaxTeamsValue] = React.useState('');
  const [savingMaxTeams, setSavingMaxTeams] = React.useState(false);
  const [enrollingChampId, setEnrollingChampId] = React.useState(null);
  const [enrollSearch, setEnrollSearch] = React.useState('');
  const [enrolling, setEnrolling] = React.useState(false);
  const [rosterClubId, setRosterClubId] = React.useState(null);
  const [rosterMembers, setRosterMembers] = React.useState([]);
  const [rosterLoading, setRosterLoading] = React.useState(false);
  const [jollyStatus, setJollyStatus] = React.useState({});
  const [jollyLoading, setJollyLoading] = React.useState(null);
  const [withdrawingClub, setWithdrawingClub] = React.useState(null);
  const [replacingClub, setReplacingClub] = React.useState(null); // { champId, oldClubId, oldClubName }
  const [replaceNewClubId, setReplaceNewClubId] = React.useState('');
  const [replaceLoading, setReplaceLoading] = React.useState(false);
  const [replaceSearch, setReplaceSearch] = React.useState('');
  const [knockoutPreviewChamp, setKnockoutPreviewChamp] = React.useState(null);

  const handleWithdrawClub = async (champId, clubId, clubName) => {
    const confirmed = window.confirm(
      isItalian
        ? `Sei sicuro di voler ritirare "${clubName}"? Tutte le partite non giocate diventeranno 0-1 a tavolino.`
        : `Are you sure you want to withdraw "${clubName}"? All unplayed matches will be set to 0-1 forfeit.`
    );
    if (!confirmed) return;
    setWithdrawingClub(clubId);
    try {
      const res = await championshipsApi.withdrawClub(champId, clubId);
      toast.success(res.data?.message || (isItalian ? 'Club ritirato!' : 'Club withdrawn!'));
      if (refreshData) refreshData();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isItalian ? 'Errore ritiro' : 'Withdrawal error'));
    } finally {
      setWithdrawingClub(null);
    }
  };

  const handleReinstateClub = async (champId, clubId, clubName) => {
    const confirmed = window.confirm(
      isItalian
        ? `Reintegrare "${clubName}" nel campionato? Le partite perse a tavolino torneranno in calendario da giocare.`
        : `Reinstate "${clubName}"? Forfeited matches will be rescheduled.`
    );
    if (!confirmed) return;
    setWithdrawingClub(clubId);
    try {
      const res = await championshipsApi.reinstateClub(champId, clubId);
      toast.success(res.data?.message || (isItalian ? 'Club reintegrato!' : 'Club reinstated!'));
      if (refreshData) refreshData();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isItalian ? 'Errore reintegro' : 'Reinstate error'));
    } finally {
      setWithdrawingClub(null);
    }
  };

  const handleReplaceClub = async () => {
    if (!replacingClub || !replaceNewClubId) return;
    setReplaceLoading(true);
    try {
      const res = await championshipsApi.replaceClub(replacingClub.champId, replacingClub.oldClubId, replaceNewClubId);
      toast.success(res.data?.message || (isItalian ? 'Club sostituito!' : 'Club replaced!'));
      setReplacingClub(null);
      setReplaceNewClubId('');
      setReplaceSearch('');
      if (refreshData) refreshData();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isItalian ? 'Errore sostituzione' : 'Replace error'));
    } finally {
      setReplaceLoading(false);
    }
  };

  React.useEffect(() => {
    if (!expandedChampClubs) return;
    const champ = championships.find(c => c.championship_id === expandedChampClubs);
    if (!champ || champ.status === 'registration') return;
    setJollyLoading(expandedChampClubs);
    championshipsApi.getJollyStatus(expandedChampClubs)
      .then(res => setJollyStatus(prev => ({ ...prev, [expandedChampClubs]: res.data || [] })))
      .catch(() => {})
      .finally(() => setJollyLoading(null));
  }, [expandedChampClubs, championships]);

  const handleEditMaxTeams = (champ) => {
    setEditingMaxTeams(champ.championship_id);
    setMaxTeamsValue(String(champ.max_teams || 20));
  };

  const handleSaveMaxTeams = async (champId) => {
    const val = parseInt(maxTeamsValue);
    if (isNaN(val) || val < 2) {
      toast.error(isItalian ? 'Inserisci un numero valido (min. 2)' : 'Enter a valid number (min. 2)');
      return;
    }
    setSavingMaxTeams(true);
    try {
      await championshipsApi.updateMaxTeams(champId, val);
      toast.success(isItalian ? `Max squadre aggiornato a ${val}` : `Max teams updated to ${val}`);
      setEditingMaxTeams(null);
      if (refreshData) refreshData();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isItalian ? 'Errore aggiornamento' : 'Update error'));
    } finally {
      setSavingMaxTeams(false);
    }
  };

  const handleEnrollClub = async (champId, clubId) => {
    setEnrolling(true);
    try {
      const res = await championshipsApi.addClub(champId, clubId);
      toast.success(res.data?.message || (isItalian ? 'Club iscritto!' : 'Club enrolled!'));
      setEnrollingChampId(null);
      setEnrollSearch('');
      if (refreshData) refreshData();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isItalian ? 'Errore iscrizione' : 'Enrollment error'));
    } finally {
      setEnrolling(false);
    }
  };

  const getAvailableClubs = (champ) => {
    const enrolledIds = new Set(champ.clubs || []);
    return clubs
      .filter(c => !enrolledIds.has(c.club_id))
      .filter(c => {
        if (!enrollSearch.trim()) return true;
        const q = enrollSearch.toLowerCase();
        return c.name?.toLowerCase().includes(q);
      });
  };

  const handleToggleRoster = async (clubId) => {
    if (rosterClubId === clubId) {
      setRosterClubId(null);
      setRosterMembers([]);
      return;
    }
    setRosterClubId(clubId);
    setRosterLoading(true);
    try {
      const res = await clubsApi.getMembers(clubId);
      setRosterMembers(res.data || []);
    } catch {
      toast.error(isItalian ? 'Errore caricamento roster' : 'Error loading roster');
      setRosterMembers([]);
    } finally {
      setRosterLoading(false);
    }
  };
  const getStatusBadge = (status) => {
    const config = {
      registration: { label: isItalian ? 'Iscrizioni Aperte' : 'Open', color: 'bg-[#00E676]/20 text-[#00E676] border-[#00E676]/30' },
      in_progress: { label: isItalian ? 'In Corso' : 'In Progress', color: 'bg-[#FFEA00]/20 text-[#FFEA00] border-[#FFEA00]/30' },
      completed: { label: isItalian ? 'Completato' : 'Completed', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
      suspended: { label: isItalian ? 'Sospeso' : 'Suspended', color: 'bg-[#FF1744]/20 text-[#FF1744] border-[#FF1744]/30' },
    };
    const s = config[status] || config.registration;
    return (
      <span className={`px-2 py-1 text-xs rounded border ${s.color}`}>
        {s.label}
      </span>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-[#0a0d15] border border-white/10"
    >
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <h3 className="font-heading text-lg uppercase tracking-wider text-white">
          {isItalian ? 'Campionati' : 'Championships'}
        </h3>
        <Button
          className="btn-primary"
          onClick={() => setShowCreateChampionshipDialog(true)}
          data-testid="create-championship-btn"
        >
          <Plus size={16} className="mr-2" />
          {isItalian ? 'Nuovo Campionato' : 'New Championship'}
        </Button>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-slate-500 uppercase tracking-wider text-xs">{isItalian ? 'Nome' : 'Name'}</TableHead>
              <TableHead className="text-slate-500 uppercase tracking-wider text-xs">Status</TableHead>
              <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-right">{isItalian ? 'Quota' : 'Fee'}</TableHead>
              <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-right">Club</TableHead>
              <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-right">Max Teams</TableHead>
              <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-center">{isItalian ? 'Gironi' : 'Groups'}</TableHead>
              <TableHead className="text-slate-500 uppercase tracking-wider text-xs">{isItalian ? 'Chiave Accesso' : 'Access Key'}</TableHead>
              <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-right">{isItalian ? 'Azioni' : 'Actions'}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {championships.map((champ) => (
              <React.Fragment key={champ.championship_id}>
                <TableRow className="border-white/10 hover:bg-white/5">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Trophy size={16} className="text-[#FFEA00]" />
                      <div>
                        <p className="font-medium text-white">{champ.name}</p>
                        <p className="text-xs text-slate-500">{champ.season}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(champ.status)}</TableCell>
                  <TableCell className="text-right">
                    {champ.entry_fee > 0 ? (
                      <span className="font-mono text-[#FFEA00]">{champ.entry_fee?.toLocaleString()} coins</span>
                    ) : (
                      <span className="text-[#00E676]">FREE</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono text-white">
                    <button
                      onClick={() => setExpandedChampClubs(expandedChampClubs === champ.championship_id ? null : champ.championship_id)}
                      className={`px-2 py-1 rounded text-sm transition-colors ${
                        expandedChampClubs === champ.championship_id
                          ? 'bg-[#00F0FF]/20 text-[#00F0FF] border border-[#00F0FF]/40'
                          : 'hover:bg-white/10 text-white'
                      }`}
                      data-testid={`expand-clubs-${champ.championship_id}`}
                    >
                      {champ.clubs?.length || 0} <Users size={12} className="inline ml-1" />
                    </button>
                  </TableCell>
                  <TableCell className="text-right font-mono text-slate-400">
                    {editingMaxTeams === champ.championship_id ? (
                      <div className="flex items-center justify-end gap-1">
                        <input
                          type="number"
                          min="2"
                          value={maxTeamsValue}
                          onChange={(e) => setMaxTeamsValue(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleSaveMaxTeams(champ.championship_id)}
                          className="w-16 bg-[#050508] border border-[#00F0FF]/30 text-white text-sm text-center rounded px-1 py-0.5 focus:outline-none focus:border-[#00F0FF]"
                          data-testid={`max-teams-input-${champ.championship_id}`}
                          autoFocus
                        />
                        <button
                          onClick={() => handleSaveMaxTeams(champ.championship_id)}
                          disabled={savingMaxTeams}
                          className="p-1 rounded hover:bg-[#00E676]/20 text-[#00E676] transition-colors disabled:opacity-50"
                          data-testid={`save-max-teams-${champ.championship_id}`}
                        >
                          <Check size={14} />
                        </button>
                        <button
                          onClick={() => setEditingMaxTeams(null)}
                          className="p-1 rounded hover:bg-red-500/20 text-red-400 transition-colors"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleEditMaxTeams(champ)}
                        className="inline-flex items-center gap-1 hover:text-[#00F0FF] transition-colors group"
                        title={isItalian ? 'Modifica max squadre' : 'Edit max teams'}
                        data-testid={`edit-max-teams-${champ.championship_id}`}
                      >
                        {champ.max_teams}
                        <Pencil size={10} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {(champ.num_groups || 1) > 1 ? (
                      <span className="px-2 py-0.5 text-xs bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/20 rounded">
                        {champ.num_groups} × {champ.teams_per_group || '?'}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-500">{isItalian ? 'Unico' : 'Single'}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <code className="bg-[#050508] px-2 py-1 rounded text-xs text-[#00E676] font-mono border border-[#00E676]/30">
                        {champ.access_key || 'N/A'}
                      </code>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6 text-slate-400 hover:text-white"
                        onClick={() => {
                          navigator.clipboard.writeText(champ.access_key || '');
                          toast.success(isItalian ? 'Chiave copiata!' : 'Key copied!');
                        }}
                        title={isItalian ? 'Copia chiave' : 'Copy key'}
                      >
                        <Copy size={12} />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6 text-[#FFEA00] hover:text-[#FFEA00] hover:bg-[#FFEA00]/10"
                        onClick={async () => {
                          try {
                            const res = await championshipsApi.regenerateKey(champ.championship_id);
                            toast.success(isItalian ? `Nuova chiave: ${res.data.access_key}` : `New key: ${res.data.access_key}`);
                            refreshData();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Errore');
                          }
                        }}
                        title={isItalian ? 'Rigenera chiave' : 'Regenerate key'}
                        data-testid={`regenerate-key-${champ.championship_id}`}
                      >
                        <RefreshCw size={12} />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {champ.status === 'registration' && (champ.clubs?.length || 0) >= 2 && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-[#00F0FF] hover:text-[#00F0FF] hover:bg-[#00F0FF]/10"
                          onClick={() => handleGenerateSchedule(champ)}
                          title={isItalian ? 'Genera Calendario (Girone all\'italiana)' : 'Generate Schedule (Round Robin)'}
                          data-testid={`generate-schedule-${champ.championship_id}`}
                        >
                          <Calendar size={14} />
                        </Button>
                      )}
                      {champ.status === 'in_progress' && (champ.clubs?.length || 0) >= 2 && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-orange-400 hover:text-orange-400 hover:bg-orange-500/10"
                          onClick={() => {
                            const target = champ.legs === 'solo_andata' ? 'andata_ritorno' : 'solo_andata';
                            handleGenerateSchedule(champ, target);
                          }}
                          title={isItalian
                            ? (champ.legs === 'solo_andata'
                                ? 'Rigenera come Andata e Ritorno'
                                : 'Rigenera come Solo Andata')
                            : (champ.legs === 'solo_andata'
                                ? 'Regenerate as Home & Away'
                                : 'Regenerate as Single Round')}
                          data-testid={`regenerate-legs-${champ.championship_id}`}
                        >
                          <Calendar size={14} />
                        </Button>
                      )}
                      {champ.status !== 'completed' && champ.status !== 'suspended' && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className={`h-8 w-8 ${champ.status === 'registration'
                            ? 'text-[#FF1744] hover:text-[#FF1744] hover:bg-[#FF1744]/10'
                            : 'text-[#00E676] hover:text-[#00E676] hover:bg-[#00E676]/10'}`}
                          onClick={() => handleToggleRegistration(champ)}
                          title={champ.status === 'registration'
                            ? (isItalian ? 'Chiudi Iscrizioni' : 'Close Registration')
                            : (isItalian ? 'Riapri Iscrizioni' : 'Reopen Registration')}
                          data-testid={`toggle-registration-${champ.championship_id}`}
                        >
                          {champ.status === 'registration' ? <Lock size={14} /> : <Unlock size={14} />}
                        </Button>
                      )}
                      {champ.status !== 'completed' && champ.status !== 'registration' && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className={`h-8 w-8 ${champ.status === 'suspended'
                            ? 'text-[#00E676] hover:text-[#00E676] hover:bg-[#00E676]/10'
                            : 'text-orange-400 hover:text-orange-400 hover:bg-orange-500/10'}`}
                          onClick={() => handleSuspendChampionship(champ)}
                          title={champ.status === 'suspended'
                            ? (isItalian ? 'Riattiva Campionato' : 'Resume Championship')
                            : (isItalian ? 'Sospendi Campionato' : 'Suspend Championship')}
                          data-testid={`suspend-championship-${champ.championship_id}`}
                        >
                          {champ.status === 'suspended' ? <PlayCircle size={14} /> : <Pause size={14} />}
                        </Button>
                      )}
                      {champ.status === 'in_progress' && champ.tournament_type === 'groups_with_knockout' && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-[#FF00E5] hover:text-[#FF00E5] hover:bg-[#FF00E5]/10"
                          onClick={() => setKnockoutPreviewChamp(champ)}
                          title={isItalian
                            ? (champ.phase === 'knockout' ? 'Rigenera Bracket (anteprima e conferma)' : 'Anteprima Bracket — Genera Fase Eliminazione')
                            : (champ.phase === 'knockout' ? 'Regenerate Bracket (preview & confirm)' : 'Bracket Preview — Generate Knockout Phase')}
                          data-testid={`${champ.phase === 'knockout' ? 'regenerate' : 'generate'}-knockout-${champ.championship_id}`}
                        >
                          <Swords size={14} />
                        </Button>
                      )}
                      {champ.status === 'in_progress' && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-[#00F0FF] hover:text-[#00F0FF] hover:bg-[#00F0FF]/10"
                          onClick={async () => {
                            if (!window.confirm(isItalian
                              ? 'Riordinare le giornate in ordine cronologico in base alle date delle partite? Questa operazione riassegna i numeri delle giornate.'
                              : 'Reorder matchdays chronologically based on match dates? This will reassign matchday numbers.')) return;
                            try {
                              const res = await championshipsApi.reorderMatchdays(champ.championship_id);
                              toast.success(res.data.message || (isItalian ? 'Giornate riordinate' : 'Matchdays reordered'));
                              refreshData();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Errore');
                            }
                          }}
                          title={isItalian ? 'Riordina Giornate per Data' : 'Reorder Matchdays by Date'}
                          data-testid={`reorder-matchdays-${champ.championship_id}`}
                        >
                          <CalendarClock size={14} />
                        </Button>
                      )}
                      {champ.status === 'in_progress' && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-[#B388FF] hover:text-[#B388FF] hover:bg-[#B388FF]/10"
                          onClick={async () => {
                            if (!window.confirm(isItalian
                              ? 'Riallineare ogni partita al proprio turno di round-robin (match_day = round)? Ripristina 13 andata + 13 ritorno correggendo eventuali giornate disallineate o extra.'
                              : 'Realign every match to its round-robin round (match_day = round)? Restores 13 first-leg + 13 second-leg matchdays, fixing any misaligned or extra matchdays.')) return;
                            try {
                              const res = await championshipsApi.realignMatchdaysByRound(champ.championship_id);
                              toast.success(res.data.message || (isItalian ? 'Giornate riallineate' : 'Matchdays realigned'));
                              refreshData();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Errore');
                            }
                          }}
                          title={isItalian ? 'Riallinea Giornate al Round (13+13)' : 'Realign Matchdays to Round (13+13)'}
                          data-testid={`realign-matchdays-${champ.championship_id}`}
                        >
                          <ListOrdered size={14} />
                        </Button>
                      )}
                      {champ.status === 'in_progress' && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-[#FFEA00] hover:text-[#FFEA00] hover:bg-[#FFEA00]/10"
                          onClick={() => handleCompleteChampionship(champ)}
                          title={isItalian ? 'Termina Campionato' : 'Complete Championship'}
                          data-testid={`complete-championship-${champ.championship_id}`}
                        >
                          <Flag size={14} />
                        </Button>
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-[#FF1744] hover:text-[#FF1744] hover:bg-[#FF1744]/10"
                        onClick={() => handleDeleteChampionship(champ)}
                        title={isItalian ? 'Elimina' : 'Delete'}
                        data-testid={`delete-championship-${champ.championship_id}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                {expandedChampClubs === champ.championship_id && (
                  <TableRow className="bg-[#0a0d14]">
                    <TableCell colSpan={8} className="p-0">
                      <div className="p-4 border-t border-[#00F0FF]/10">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-xs text-slate-400 uppercase tracking-wider">{isItalian ? 'Club Iscritti' : 'Enrolled Clubs'}</p>
                          {champ.status !== 'completed' && champ.status !== 'suspended' && (
                            <button
                              onClick={() => setEnrollingChampId(enrollingChampId === champ.championship_id ? null : champ.championship_id)}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-[#00E676]/10 text-[#00E676] border border-[#00E676]/30 hover:bg-[#00E676]/20 transition-colors"
                              data-testid={`enroll-club-btn-${champ.championship_id}`}
                            >
                              <UserPlus size={14} />
                              {isItalian ? 'Iscrivi Club' : 'Enroll Club'}
                            </button>
                          )}
                        </div>

                        {enrollingChampId === champ.championship_id && (
                          <div className="mb-4 p-3 bg-[#050508] border border-[#00E676]/20 space-y-2" data-testid={`enroll-panel-${champ.championship_id}`}>
                            <div className="relative">
                              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                              <input
                                type="text"
                                value={enrollSearch}
                                onChange={(e) => setEnrollSearch(e.target.value)}
                                placeholder={isItalian ? 'Cerca club per nome...' : 'Search club by name...'}
                                className="w-full bg-[#0a0d15] border border-white/10 text-white text-sm pl-8 pr-3 py-2 focus:outline-none focus:border-[#00E676]/50"
                                data-testid={`enroll-search-${champ.championship_id}`}
                                autoFocus
                              />
                            </div>
                            <div className="max-h-48 overflow-y-auto space-y-1">
                              {getAvailableClubs(champ).length === 0 ? (
                                <p className="text-xs text-slate-500 py-2 text-center">
                                  {isItalian ? 'Nessun club disponibile' : 'No clubs available'}
                                </p>
                              ) : (
                                getAvailableClubs(champ).map(c => (
                                  <button
                                    key={c.club_id}
                                    onClick={() => handleEnrollClub(champ.championship_id, c.club_id)}
                                    disabled={enrolling}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[#00E676]/10 transition-colors disabled:opacity-50"
                                    data-testid={`enroll-option-${c.club_id}`}
                                  >
                                    <Plus size={14} className="text-[#00E676] flex-shrink-0" />
                                    <span className="text-white truncate">{c.name}</span>
                                  </button>
                                ))
                              )}
                            </div>
                          </div>
                        )}

                        {champ.clubs && champ.clubs.length > 0 ? (
                          <div className="space-y-2">
                            <div className="flex flex-wrap gap-2">
                              {champ.clubs.map(clubId => {
                                const club = clubs.find(c => c.club_id === clubId);
                                const clubName = club?.name || clubId;
                                const isSelected = rosterClubId === clubId;
                                const isWithdrawn = (champ.withdrawn_clubs || []).includes(clubId);
                                return (
                                  <div
                                    key={clubId}
                                    className={`flex items-center gap-2 border rounded-lg px-3 py-2 cursor-pointer transition-all ${
                                      isWithdrawn
                                        ? 'bg-[#FF1744]/5 border-[#FF1744]/30 opacity-70'
                                        : isSelected
                                        ? 'bg-[#00F0FF]/10 border-[#00F0FF]/40'
                                        : 'bg-[#151921] border-white/10 hover:border-[#00F0FF]/20'
                                    }`}
                                    data-testid={`club-chip-${clubId}`}
                                  >
                                    <button
                                      onClick={() => handleToggleRoster(clubId)}
                                      className={`text-sm hover:text-[#00F0FF] transition-colors ${isWithdrawn ? 'text-[#FF1744] line-through' : 'text-white'}`}
                                      title={isItalian ? 'Mostra roster' : 'Show roster'}
                                      data-testid={`roster-btn-${clubId}`}
                                    >
                                      {clubName}
                                    </button>
                                    {isWithdrawn && (
                                      <>
                                        <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase bg-[#FF1744]/20 text-[#FF1744] border border-[#FF1744]/30 rounded" data-testid={`withdrawn-badge-${clubId}`}>
                                          {isItalian ? 'Ritirato' : 'Withdrawn'}
                                        </span>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleReinstateClub(champ.championship_id, clubId, clubName); }}
                                          disabled={withdrawingClub === clubId}
                                          className="px-1.5 py-0.5 text-[9px] font-bold uppercase bg-[#00F0FF]/15 text-[#00F0FF] border border-[#00F0FF]/30 rounded hover:bg-[#00F0FF]/25 transition-colors cursor-pointer disabled:opacity-50"
                                          title={isItalian ? 'Reintegra il club e rischedula le partite a tavolino' : 'Reinstate club and reschedule forfeited matches'}
                                          data-testid={`reinstate-club-${clubId}`}
                                        >
                                          {withdrawingClub === clubId ? '...' : (isItalian ? 'Reintegra' : 'Reinstate')}
                                        </button>
                                      </>
                                    )}
                                    {champ.status === 'in_progress' && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); setReplacingClub({ champId: champ.championship_id, oldClubId: clubId, oldClubName: clubName }); setReplaceNewClubId(''); setReplaceSearch(''); }}
                                        className="px-1.5 py-0.5 text-[9px] font-bold uppercase bg-[#00E676]/15 text-[#00E676] border border-[#00E676]/30 rounded hover:bg-[#00E676]/25 transition-colors cursor-pointer"
                                        title={isItalian ? 'Sostituisci con un altro club (eredita calendario e tavolini)' : 'Replace with another club'}
                                        data-testid={`replace-club-${clubId}`}
                                      >
                                        {isItalian ? 'Sostituisci' : 'Replace'}
                                      </button>
                                    )}
                                    {!isWithdrawn && (champ.status === 'in_progress' || champ.status === 'suspended') && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleWithdrawClub(champ.championship_id, clubId, clubName); }}
                                        disabled={withdrawingClub === clubId}
                                        className="ml-1 p-0.5 rounded hover:bg-orange-500/20 text-orange-400 hover:text-orange-300 transition-colors disabled:opacity-50"
                                        title={isItalian ? 'Ritira squadra (tavolino 0-1)' : 'Withdraw team (0-1 forfeit)'}
                                        data-testid={`withdraw-club-${clubId}`}
                                      >
                                        {withdrawingClub === clubId ? (
                                          <span className="w-3 h-3 border border-orange-400 border-t-transparent rounded-full animate-spin inline-block" />
                                        ) : (
                                          <Ban size={14} />
                                        )}
                                      </button>
                                    )}
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleRemoveClubFromChampionship(champ.championship_id, clubId, clubName); }}
                                      disabled={removingClub === clubId}
                                      className="ml-1 p-0.5 rounded hover:bg-red-500/20 text-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
                                      title={isItalian ? 'Rimuovi dal campionato' : 'Remove from championship'}
                                      data-testid={`remove-club-${clubId}`}
                                    >
                                      {removingClub === clubId ? (
                                        <span className="w-3 h-3 border border-red-400 border-t-transparent rounded-full animate-spin inline-block" />
                                      ) : (
                                        <X size={14} />
                                      )}
                                    </button>
                                  </div>
                                );
                              })}
                            </div>

                            {rosterClubId && champ.clubs.includes(rosterClubId) && (
                              <div className="mt-3 p-3 bg-[#050508] border border-[#00F0FF]/15 rounded" data-testid={`roster-panel-${rosterClubId}`}>
                                <p className="text-xs text-[#00F0FF] uppercase tracking-wider mb-2 font-bold">
                                  {isItalian ? 'Roster' : 'Roster'} — {clubs.find(c => c.club_id === rosterClubId)?.name}
                                </p>
                                {rosterLoading ? (
                                  <div className="flex items-center gap-2 py-3">
                                    <span className="w-4 h-4 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
                                    <span className="text-xs text-slate-400">Loading...</span>
                                  </div>
                                ) : rosterMembers.length === 0 ? (
                                  <p className="text-xs text-slate-500 py-2">{isItalian ? 'Nessun membro' : 'No members'}</p>
                                ) : (
                                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                    {rosterMembers.map(m => (
                                      <div
                                        key={m.user_id}
                                        className="flex items-center gap-2 px-3 py-2 bg-[#0a0d15] border border-white/5 rounded"
                                        data-testid={`roster-member-${m.user_id}`}
                                      >
                                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#00F0FF]/20 to-[#00E676]/20 flex items-center justify-center text-xs font-bold text-[#00F0FF] flex-shrink-0">
                                          {m.gamertag?.charAt(0)?.toUpperCase() || '?'}
                                        </div>
                                        <div className="min-w-0">
                                          <p className="text-sm text-white truncate">{m.gamertag}</p>
                                          <p className="text-[10px] text-slate-500 truncate">
                                            {m.player_role ? <span className="text-[#00E676]">{m.player_role}</span> : m.email}
                                            {m.role === 'captain' && <span className="ml-1 text-[#FFEA00]">(C)</span>}
                                          </p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Jolly Status */}
                            {champ.status !== 'registration' && jollyStatus[champ.championship_id] && jollyStatus[champ.championship_id].length > 0 && (
                              <div className="mt-4 p-3 bg-[#050508] border border-[#FFEA00]/15 rounded" data-testid={`jolly-status-${champ.championship_id}`}>
                                <p className="text-xs text-[#FFEA00] uppercase tracking-wider mb-2 font-bold flex items-center gap-1.5">
                                  <Sparkles size={14} />
                                  {isItalian ? 'Stato Jolly' : 'Jolly Status'}
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                  {jollyStatus[champ.championship_id].map(js => (
                                    <div
                                      key={js.club_id}
                                      className="flex items-center justify-between px-3 py-2 bg-[#0a0d15] border border-white/5 rounded"
                                      data-testid={`jolly-club-${js.club_id}`}
                                    >
                                      <span className="text-sm text-white truncate mr-2">{js.club_name}</span>
                                      <div className="flex items-center gap-1 shrink-0">
                                        {Array.from({ length: js.jolly_free }).map((_, i) => (
                                          <div
                                            key={i}
                                            className={`w-2.5 h-2.5 rounded-full ${
                                              i < (js.jolly_used || 0)
                                                ? (i >= js.jolly_free ? 'bg-[#FFEA00]' : 'bg-[#FF1744]')
                                                : 'bg-[#00E676]'
                                            }`}
                                            title={i < (js.jolly_used || 0) 
                                              ? (isItalian ? 'Usato' : 'Used') 
                                              : (isItalian ? 'Disponibile' : 'Available')}
                                          />
                                        ))}
                                        <span className="ml-1.5 text-xs text-slate-400">
                                          {js.jolly_free_remaining}/{js.jolly_free}
                                        </span>
                                        {(js.jolly_paid || 0) > 0 && (
                                          <span className="ml-1 text-[10px] text-[#FFEA00] bg-[#FFEA00]/10 px-1.5 py-0.5 rounded">
                                            +{js.jolly_paid} {isItalian ? 'acquistati' : 'purchased'}
                                          </span>
                                        )}
                                        {js.jolly_pending > 0 && (
                                          <span className="ml-1 text-[10px] text-[#FFEA00] bg-[#FFEA00]/10 px-1.5 py-0.5 rounded">
                                            {js.jolly_pending} {isItalian ? 'in attesa' : 'pending'}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {jollyLoading === champ.championship_id && (
                              <div className="mt-4 flex items-center gap-2 text-xs text-slate-400">
                                <span className="w-3 h-3 border-2 border-[#FFEA00] border-t-transparent rounded-full animate-spin" />
                                {isItalian ? 'Caricamento Jolly...' : 'Loading Jolly...'}
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-slate-500">{isItalian ? 'Nessun club iscritto' : 'No clubs enrolled'}</p>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
        {championships.length === 0 && (
          <div className="p-8 text-center text-slate-500">
            <Trophy size={32} className="mx-auto mb-2 opacity-50" />
            <p>{isItalian ? 'Nessun campionato' : 'No championships'}</p>
          </div>
        )}
      </div>

      {/* Replace Club Dialog */}
      {replacingClub && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setReplacingClub(null)}>
          <div className="bg-[#0a0d15] border border-[#00E676]/20 rounded-lg p-6 max-w-md w-full space-y-4" onClick={e => e.stopPropagation()} data-testid="replace-club-dialog">
            <div className="flex items-center gap-2">
              <UserPlus size={20} className="text-[#00E676]" />
              <h3 className="text-white font-bold uppercase tracking-wider text-sm">
                {isItalian ? 'Sostituisci Club' : 'Replace Club'}
              </h3>
            </div>
            <p className="text-sm text-slate-400">
              {isItalian
                ? `Sostituisci "${replacingClub.oldClubName}" con un nuovo club: eredita il calendario (partite programmate e tavolini vinti), i tavolini del vecchio club vengono rimossi e le partite giocate davvero restano.`
                : `Replace "${replacingClub.oldClubName}" with a new club. Forfeit matches will be reset to scheduled.`}
            </p>
            <div>
              <input
                type="text"
                value={replaceSearch}
                onChange={(e) => setReplaceSearch(e.target.value)}
                placeholder={isItalian ? 'Cerca club...' : 'Search club...'}
                className="w-full bg-[#050508] border border-white/10 text-white px-3 py-2 text-sm rounded mb-2"
                data-testid="replace-search-input"
              />
              <div className="max-h-48 overflow-y-auto space-y-1">
                {clubs
                  .filter(c => {
                    const champ = championships.find(ch => ch.championship_id === replacingClub.champId);
                    const enrolledClubs = champ?.clubs || [];
                    return !enrolledClubs.includes(c.club_id) && c.name.toLowerCase().includes(replaceSearch.toLowerCase());
                  })
                  .slice(0, 10)
                  .map(c => (
                    <button
                      key={c.club_id}
                      onClick={() => setReplaceNewClubId(c.club_id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded text-sm text-left transition-colors ${
                        replaceNewClubId === c.club_id
                          ? 'bg-[#00E676]/15 border border-[#00E676]/40 text-[#00E676]'
                          : 'bg-[#050508] border border-white/10 text-white hover:border-white/20'
                      }`}
                      data-testid={`replace-option-${c.club_id}`}
                    >
                      {c.logo && <img src={c.logo} alt="" className="w-5 h-5 object-cover rounded-sm flex-shrink-0" />}
                      <span className="truncate">{c.name}</span>
                      {replaceNewClubId === c.club_id && <Check size={14} className="ml-auto flex-shrink-0" />}
                    </button>
                  ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleReplaceClub}
                disabled={!replaceNewClubId || replaceLoading}
                className="flex-1 bg-[#00E676] hover:bg-[#00E676]/90 text-black font-bold uppercase tracking-wider"
                data-testid="confirm-replace-btn"
              >
                {replaceLoading ? (
                  <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" /> ...</span>
                ) : (
                  isItalian ? 'Conferma Sostituzione' : 'Confirm Replace'
                )}
              </Button>
              <Button
                onClick={() => setReplacingClub(null)}
                variant="outline"
                className="border-white/10"
              >
                {isItalian ? 'Annulla' : 'Cancel'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <KnockoutPreviewDialog
        open={!!knockoutPreviewChamp}
        onOpenChange={(o) => { if (!o) setKnockoutPreviewChamp(null); }}
        championship={knockoutPreviewChamp}
        isItalian={isItalian}
        onConfirmed={(champId) => {
          setKnockoutPreviewChamp(null);
          setTimeout(() => {
            window.location.href = `/tornei?expand=${champId}#bracket`;
          }, 600);
        }}
      />
    </motion.div>
  );
};
